<template>
  <UIPopup
    v-model="modelValue"
    size="big"
    class="!z-20 bg-black-100/50"
    closeable
    hide-buttons
    hide-close-button
    @close="modelValue = false"
  >
    <template v-if="isMobile" #outside>
      <button class="absolute right-4 top-4" @click="modelValue = false">
        <UIIcon name="big-close" class="text-white" />
      </button>
    </template>
    <template #body>
      <div v-if="item" class="flex flex-col md:flex-row">
        <div class="w-full p-4 md:w-1/2">
          <ClientOnly>
            <Swiper class="!h-full bg-white">
              <SwiperSlide v-for="(image, i) in gallery" :key="i" class="!size-full cursor-pointer select-none">
                <img :src="image" :alt="`preview-${i}`" class="size-full max-h-[250px] object-contain lg:max-h-none" />
              </SwiperSlide>
            </Swiper>
          </ClientOnly>
        </div>
        <div class="flex size-full flex-1 flex-col bg-black-03 p-6 text-left md:w-1/2">
          <div class="mb-8 flex gap-2">
            <PropertyTabsUnitTag
              v-if="roomsCountLabel"
              :label="roomsCountLabel"
            />
            <PropertyTabsUnitTag
              v-if="unitTypeLabel"
              :label="unitTypeLabel"
            />
          </div>
          <h4 class="mb-2.5">{{ item.rooms_count.en.length ? item.rooms_count.en : item.title.en }}</h4>
          <div class="text-body mb-1 flex items-center gap-2">
            <p>{{ $t('property.unitInfo.type') }} {{ index + 1 }}</p>
            •
            <p>{{ item.sale_units_count }} {{ $t('property.unitInfo.units') }}</p>
            •
            <p>{{ $t('property.unitInfo.from') }} {{ item.area_min.m2 }} m<sup>2</sup></p>
          </div>
          <p class="text-subhead-2 mb-6">
            {{ aedFormatter.format(Number(item.price.min)) }} {{ item.price.currency }} -
            {{ aedFormatter.format(Number(item.price.max)) }} {{ item.price.currency }}
          </p>
          <div class="flex flex-wrap gap-8">
            <div class="flex gap-2">
              <div class="size-10 rounded bg-white p-2">
                <UIIcon name="area"></UIIcon>
              </div>
              <div>
                <p class="text-caption mb-px text-black-60">{{ $t('property.unitInfo.totalS') }}</p>
                <p class="text-subhead-2">{{ item.area_min.m2 }} m2</p>
              </div>
            </div>
            <div class="flex gap-2">
              <div class="size-10 rounded bg-white p-2">
                <UIIcon name="bed"></UIIcon>
              </div>
              <div>
                <p class="text-caption mb-px text-black-60">{{ $t('property.unitInfo.rooms') }}</p>
                <p class="text-subhead-2">
                  {{ item.rooms_count.en.length ? item.rooms_count.en : '' }}
                </p>
              </div>
            </div>
            <div class="flex gap-2">
              <div class="size-10 rounded bg-white p-2">
                <UIIcon name="balcony"></UIIcon>
              </div>
              <div>
                <p class="text-caption mb-px text-black-60">{{ $t('property.unitInfo.balconyS') }}</p>
                <p class="text-subhead-2">
                  {{ item.area_balcony_min.m2.length ? item.area_balcony_min.m2 + 'm2' : '-' }}
                </p>
              </div>
            </div>
          </div>
          <div class="mt-2 flex gap-2">
            <PropertyTabsUnitTag
              v-if="complex.sales_status?.en?.length"
              dark
              :label="complex.sales_status.en"
            />
            <PropertyTabsUnitTag
              dark
              :label="isNotFinished ? $t('property.unitInfo.notFinished') : $t('property.unitInfo.finished')"
            />
            <PropertyTabsUnitTag
              dark
              :label="`${$t('property.unitInfo.plannedFor')} ${format(
                new Date(complex.planned_completion_at),
                'QQQ yyyy'
              )}`"
            />
          </div>
        </div>
      </div>
    </template>
  </UIPopup>
</template>

<script setup lang="ts">
import { format, isFuture } from 'date-fns'
import { Swiper, SwiperSlide } from 'swiper/vue'
import type { Apartment, PropertyDetailed, Level } from '../../types/properties'
import 'swiper/css'

const modelValue = defineModel<boolean>()

const { isMobile } = useAppBreakpoints()

const aedFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
})

type Props = {
  item: Apartment
  index: number
  complex: PropertyDetailed
}

const props = defineProps<Props>()

const gallery = computed(() => {
  return props.item.levels_photos.flatMap((item: Level) => item.image)
})

const isNotFinished = computed(() => {
  return isFuture(new Date(props.complex.planned_completion_at))
})

const roomsCountLabel = computed(() => {
  return props.item.rooms_count.en.length ? props.item.rooms_count.en : null
})

const unitTypeLabel = computed(() => {
  return useUnitTypeLabel(props.item.type)
})
</script>

<style scoped lang="scss">
@use '@/styles/variables.scss' as *;

:deep() {
  .popup__content {
    width: 100%;
    max-width: 900px !important;
    max-height: 95vh;
    overflow: hidden;
    background: #ffffff;
    box-shadow: none;
    border-radius: 12px;
    padding: 0 !important;
    position: relative;

    &-header {
      width: 100%;
      position: absolute;
      top: 24px;
      right: 30px;
    }

    &-body {
      margin: 0;
      padding: 0;
    }
  }
}

h4 {
  @media screen and (max-width: 767px) {
    font-size: 16px;
    line-height: 22px;
  }
}

.text-body {
  @media screen and (max-width: 767px) {
    font-size: 12px;
    line-height: 16px;
  }
}

.text-subhead-2 {
  @media screen and (max-width: 767px) {
    font-size: 14px;
    line-height: 20px;
  }
}

.text-caption {
  @media screen and (max-width: 767px) {
    font-size: 10px;
    line-height: 14px;
  }
}

:deep(.text-caption-1) {
  @media screen and (max-width: 767px) {
    font-size: 10px;
    line-height: 14px;
    padding: 4px 8px;
  }
}
</style>
